import React, { useState, useEffect } from 'react'
import {
  Card,
  Avatar,
  Button,
  Tag,
  Descriptions,
  Drawer,
  message,
  Image,
  Space,
  Result,
  Spin
} from 'antd'
import {
  UserOutlined,
  InstagramOutlined,
  FacebookOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons'
import { useParams } from 'react-router-dom'
import api from '../utils/api'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import useGoBack from '../hooks/useGoBack'
import { PHOTO_URL } from '../utils/apiutils'
import { categories } from '../utils/utils'
import { formatTz } from '../utils/timezone'

const Container = styled.div``
const BackButton = styled(Button)`
  margin-bottom: 16px;
`

const StyledCard = styled(Card)`
  margin-bottom: 16px;
  .ant-card-meta-title {
    font-size: 18px;
  }
  .ant-card-meta-description {
    font-size: 14px;
  }
`

const StyledButton = styled(Button)`
  margin-top: 8px;
  background-color: #f5f5f5;
  border: none;
  color: #d57d99;
  &:hover {
    background-color: #f5f5f5 !important;
    color: #d57d99 !important;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`

const ActionButton = styled(Button)`
  border-radius: 20px;
  padding: 4px 15px;
  height: auto;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:not(:last-child) {
    margin-right: 8px;
  }
`

const InviteButton = styled(ActionButton)`
  background-color: #1890ff;
  border-color: #1890ff;
  color: white;

  &:hover,
  &:focus {
    background-color: #40a9ff;
    border-color: #40a9ff;
  }
`

const RejectButton = styled(ActionButton)`
  background-color: white;
  border-color: #ff4d4f;
  color: #ff4d4f;

  &:hover,
  &:focus {
    background-color: #fff1f0;
    border-color: #ff7875;
    color: #ff7875;
  }
`

const InvitedButton = styled(ActionButton)`
  background-color: #52c41a;
  border-color: #52c41a;
  color: white;
  cursor: default;

  &:hover,
  &:focus {
    background-color: #52c41a !important;
    border-color: #52c41a !important;
    color: white !important;
  }
`

const RejectedButton = styled(ActionButton)`
  background-color: #ff4d4f;
  border-color: #ff4d4f;
  color: white;
  cursor: default;

  &:hover,
  &:focus {
    background-color: #ff4d4f !important;
    border-color: #ff4d4f !important;
    color: white !important;
  }
`

const UserInfo = styled.div`
  margin-bottom: 8px;
  p {
    margin: 0;
  }
`

const getCategoryName = (types) => {
  if (typeof types === 'string') {
    return types
      .split(',')
      .map((type) => {
        const category = categories.find((cat) => cat.type === type.trim())
        return category ? category.name : type.trim()
      })
      .join(', ')
  }
  return types
}

const Applicants = () => {
  const navigate = useNavigate()
  const goBack = useGoBack('/recruitment-dashboard')
  const { recruitmentId } = useParams()
  const { businessId } = useAuth()
  const { hasFeature, permissionsLoaded } = useMembership()
  const [applicants, setApplicants] = useState([])
  const [jobInfo, setJobInfo] = useState(null)
  const [selectedApplicant, setSelectedApplicant] = useState(null)
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [selectedApplicants, setSelectedApplicants] = useState([])
  const [rejectedApplicants, setRejectedApplicants] = useState([])

  useEffect(() => {
    if (!permissionsLoaded || !hasFeature('recruitment')) return
    fetchApplicants()
  }, [permissionsLoaded, hasFeature])

  const fetchApplicants = async () => {
    try {
      const response = await api.get(
        `/api/business_B/${businessId}/recruitments/${recruitmentId}/applicants`
      )
      setApplicants(response.data.data.applicants)
      setSelectedApplicants(response.data.data.selectedApplicants || [])
      setRejectedApplicants(response.data.data.rejectedApplicants || [])
      setJobInfo({
        jobTitle: response.data.data.jobTitle,
        jobDescription: response.data.data.jobDescription,
        recruitmentType: response.data.data.recruitmentType,
        dateTimeRange: response.data.data.dateTimeRange,
        location: response.data.data.location,
        requiredNumber: response.data.data.requiredNumber,
        status: response.data.data.status
      })
    } catch (error) {
      console.error('獲取應聘者信息失敗:', error)
      message.error('獲取應聘者信息失敗')
    }
  }

  const handleInvite = async (applicantId) => {
    try {
      await api.put(
        `/api/business_B/${businessId}/recruitments/${recruitmentId}/select/${applicantId}`
      )
      message.success('成功邀請應聘者')
      // 更新本地状态
      setSelectedApplicants((prevSelected) => [
        ...prevSelected,
        { user: applicantId, status: 'invited' }
      ])
      // 如果之前被拒绝，从拒绝列表中移除
      setRejectedApplicants((prevRejected) =>
        prevRejected.filter((id) => id !== applicantId)
      )
    } catch (error) {
      console.error('邀請應聘者失敗:', error)
      message.error(error.response.data.error)
    }
  }

  const handleReject = async (applicantId) => {
    try {
      await api.put(
        `/api/business_B/${businessId}/recruitments/${recruitmentId}/reject/${applicantId}`
      )
      message.success('已拒絕應聘者')
      // 更新本地状态
      setRejectedApplicants((prevRejected) => [...prevRejected, applicantId])
      // 如果之前被选中，从选中列表中移除
      setSelectedApplicants((prevSelected) =>
        prevSelected.filter((item) => item.user !== applicantId)
      )
    } catch (error) {
      console.error('拒絕應聘者失敗:', error)
      message.error(error.response.data.error)
    }
  }

  const showApplicantDetails = (applicant) => {
    setSelectedApplicant(applicant)
    setDrawerVisible(true)
  }

  const isApplicantSelected = (applicantId) => {
    return selectedApplicants.some(
      (item) => item.user === applicantId && item.status === 'accepted'
    )
  }

  const isApplicantInvited = (applicantId) => {
    return selectedApplicants.some(
      (item) => item.user === applicantId && item.status === 'invited'
    )
  }

  const isApplicantRejected = (applicantId) => {
    return rejectedApplicants.includes(applicantId)
  }

  const renderApplicantStatus = (applicant) => {
    if (isApplicantSelected(applicant._id)) {
      return (
        <Tag color='green' icon={<CheckCircleOutlined />}>
          已選中
        </Tag>
      )
    } else if (isApplicantInvited(applicant._id)) {
      return (
        <Tag color='blue' icon={<ClockCircleOutlined />}>
          已邀請
        </Tag>
      )
    } else if (isApplicantRejected(applicant._id)) {
      return (
        <Tag color='red' icon={<CloseCircleOutlined />}>
          已婉拒
        </Tag>
      )
    } else {
      return (
        <Tag color='default' icon={<ClockCircleOutlined />}>
          待處理
        </Tag>
      )
    }
  }

  const renderActionButtons = (applicant) => {
    if (isApplicantSelected(applicant._id)) {
      return (
        <InvitedButton icon={<CheckCircleOutlined />}>已選中</InvitedButton>
      )
    } else if (isApplicantInvited(applicant._id)) {
      return (
        <InvitedButton icon={<ClockCircleOutlined />}>已邀請</InvitedButton>
      )
    } else if (isApplicantRejected(applicant._id)) {
      return (
        <RejectedButton icon={<CloseCircleOutlined />}>已婉拒</RejectedButton>
      )
    } else {
      return (
        <>
          <InviteButton
            icon={<CheckCircleOutlined />}
            onClick={() => handleInvite(applicant._id)}
          >
            選中
          </InviteButton>
          <RejectButton
            icon={<CloseCircleOutlined />}
            onClick={() => handleReject(applicant._id)}
          >
            婉拒
          </RejectButton>
        </>
      )
    }
  }

  if (!permissionsLoaded) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size='large' tip='正在載入權限...' />
      </div>
    )
  }

  if (!hasFeature('recruitment')) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
          width: '100%'
        }}
      >
        <Result
          status='403'
          title='功能限制'
          subTitle='此功能僅限品牌方案用戶使用，請升級您的會員方案。'
          extra={
            <Button
              type='primary'
              onClick={() => navigate('/settings/membership')}
            >
              查看會員方案
            </Button>
          }
        />
      </div>
    )
  }

  return (
    <Container>
      <BackButton onClick={goBack}>
        <ArrowLeftOutlined /> 返回
      </BackButton>
      {jobInfo && (
        <StyledCard title={jobInfo.jobTitle} style={{ marginBottom: 20 }}>
          <p>{jobInfo.jobDescription}</p>
          <Descriptions column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label='招聘類型'>
              {(() => {
                switch (jobInfo.recruitmentType) {
                  case 'experienceModel':
                    return '體驗模特兒(純體驗)'
                  case 'reciprocalModel':
                    return '互惠模特兒(可幫忙宣傳與發文)'
                  default:
                    return jobInfo.recruitmentType
                }
              })()}
            </Descriptions.Item>
            <Descriptions.Item label='狀態'>
              {(() => {
                switch (jobInfo.status) {
                  case 'active':
                    return '進行中'
                  case 'closed':
                    return '已關閉'
                  default:
                    return jobInfo.status
                }
              })()}
            </Descriptions.Item>
            <Descriptions.Item label='開始時間'>
              {formatTz(jobInfo.dateTimeRange.start, 'YYYY/MM/DD HH:mm')}
            </Descriptions.Item>
            <Descriptions.Item label='結束時間'>
              {formatTz(jobInfo.dateTimeRange.end, 'YYYY/MM/DD HH:mm')}
            </Descriptions.Item>
            <Descriptions.Item label='地點'>{`${jobInfo.location.city} ${jobInfo.location.district}`}</Descriptions.Item>
            <Descriptions.Item label='需求人數'>
              {`${jobInfo.requiredNumber ? jobInfo.requiredNumber : '不限'}`}
            </Descriptions.Item>
          </Descriptions>
        </StyledCard>
      )}
      <h2>應聘者列表</h2>
      <p style={{ color: '#999' }}>
        請注意，若選中應聘者，將無法取消，請謹慎選擇，若選擇達到設定人數上限，本招聘將自動被關閉，若需重啟請重新設定人數上限。
      </p>
      {applicants.map((applicant) => (
        <StyledCard key={applicant._id}>
          <Card.Meta
            avatar={
              <Avatar
                src={`${PHOTO_URL}${applicant.avatar}`}
                size={64}
                icon={<UserOutlined />}
              />
            }
            title={
              <Space>
                {applicant.name}
                {renderApplicantStatus(applicant)}
              </Space>
            }
            description={
              <UserInfo>
                <p>用戶名: {applicant.username}</p>
                <StyledButton onClick={() => showApplicantDetails(applicant)}>
                  查看詳細資料
                </StyledButton>
              </UserInfo>
            }
          />
          <ButtonContainer>{renderActionButtons(applicant)}</ButtonContainer>
        </StyledCard>
      ))}

      <Drawer
        title='應聘者詳細資料'
        placement='right'
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={window.innerWidth > 768 ? 600 : '100%'}
        bodyStyle={{ paddingBottom: 80 }}
      >
        {selectedApplicant && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label='姓名'>
              {selectedApplicant.name}
            </Descriptions.Item>
            <Descriptions.Item label='用戶名'>
              {selectedApplicant.username}
            </Descriptions.Item>
            <Descriptions.Item label='皮膚狀態'>
              {(() => {
                switch (selectedApplicant.modelProfile.skinType) {
                  case 'combination':
                    return '混合性'
                  case 'dry':
                    return '偏乾'
                  case 'oily':
                    return '偏油'
                  default:
                    return selectedApplicant.modelProfile.skinType
                }
              })()}
            </Descriptions.Item>
            <Descriptions.Item label='髮質'>
              {(() => {
                switch (selectedApplicant.modelProfile.hairType) {
                  case 'coarse':
                    return '粗硬'
                  case 'fine':
                    return '細軟'
                  case 'damaged':
                    return '受損'
                  case 'normal':
                    return '適中'
                  default:
                    return selectedApplicant.modelProfile.hairType
                }
              })()}
            </Descriptions.Item>
            <Descriptions.Item label='頭髮長度'>
              {(() => {
                switch (selectedApplicant.modelProfile.hairLength) {
                  case 'short':
                    return '短'
                  case 'medium':
                    return '中'
                  case 'long':
                    return '長'
                  default:
                    return selectedApplicant.modelProfile.hairLength
                }
              })()}
            </Descriptions.Item>
            <Descriptions.Item label='指甲狀況'>
              {(() => {
                switch (selectedApplicant.modelProfile.nailCondition) {
                  case 'brittle':
                    return '斷裂'
                  case 'normal':
                    return '正常'
                  case 'ingrown':
                    return '嵌甲'
                  default:
                    return selectedApplicant.modelProfile.nailCondition
                }
              })()}
            </Descriptions.Item>
            <Descriptions.Item label='身體狀況'>
              {(() => {
                switch (selectedApplicant.modelProfile.bodyCondition) {
                  case 'pregnant':
                    return '懷孕'
                  case 'breastfeeding':
                    return '哺乳'
                  case 'none':
                    return '無'
                  default:
                    return selectedApplicant.modelProfile.bodyCondition
                }
              })()}
            </Descriptions.Item>
            <Descriptions.Item label='過敏情況'>
              {selectedApplicant.modelProfile.isAllergic ? '是' : '否'}
              {selectedApplicant.modelProfile.allergyDetails &&
                `: ${selectedApplicant.modelProfile.allergyDetails}`}
            </Descriptions.Item>
            <Descriptions.Item label='希望參與項目類型'>
              {getCategoryName(
                selectedApplicant.modelProfile.preferredCategories[0]
              )}
            </Descriptions.Item>
            <Descriptions.Item label='體驗類型'>
              {(() => {
                switch (selectedApplicant.modelProfile.recruitmentType) {
                  case 'experienceModal':
                    return '體驗模特兒(純體驗)'
                  case 'reciprocalModel':
                    return '互惠模特兒(可幫忙宣傳與發文)'
                  default:
                    return selectedApplicant.modelProfile.recruitmentType
                }
              })()}
            </Descriptions.Item>
            <Descriptions.Item label='期望收費與否'>
              {(() => {
                switch (selectedApplicant.modelProfile.expectPayment) {
                  case 'yes':
                    return '是'
                  case 'no':
                    return '否'
                  case 'maybe':
                    return '不確定，看情況'
                  default:
                    return selectedApplicant.modelProfile.expectPayment
                }
              })()}
            </Descriptions.Item>
            <Descriptions.Item label='是否需要報酬'>
              {(() => {
                switch (selectedApplicant.modelProfile.needPayment) {
                  case 'yes':
                    return '是'
                  case 'no':
                    return '否'
                  case 'maybe':
                    return '不確定，看情況'
                  default:
                    return selectedApplicant.modelProfile.needPayment
                }
              })()}
            </Descriptions.Item>
            <Descriptions.Item label='模特類型'>
              {(() => {
                switch (selectedApplicant.modelProfile.modelType) {
                  case 'model':
                    return '素人模特兒'
                  case 'modelWithFans':
                    return '自帶粉絲模特兒'
                  case 'kol':
                    return 'KOL'
                  case 'influencer':
                    return '網紅'
                  case 'modelAndInfluencer':
                    return '模特兒兼網紅'
                  case 'other':
                    return '其他'
                  default:
                    return selectedApplicant.modelProfile.modelType
                }
              })()}
            </Descriptions.Item>
            <Descriptions.Item label='粉絲規模'>
              {(() => {
                switch (selectedApplicant.modelProfile.followerCount) {
                  case '1000':
                    return '1000-5000'
                  case '5000':
                    return '5000-10000'
                  case '10000':
                    return '10000-50000'
                  case '100000':
                    return '50000-100000'
                  case '1000000':
                    return '100000以上'
                  case 'other':
                    return '其他'
                  default:
                    return selectedApplicant.modelProfile.followerCount
                }
              })()}
            </Descriptions.Item>
            <Descriptions.Item label='社交媒體' span={2}>
              <Tag icon={<InstagramOutlined />}>
                {selectedApplicant.modelProfile.instagramUrl}
              </Tag>
              <Tag icon={<FacebookOutlined />}>
                {selectedApplicant.modelProfile.facebookUrl}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label='自我介紹' span={2}>
              {selectedApplicant.modelProfile.aboutMe}
            </Descriptions.Item>
            <Descriptions.Item label='照片' span={2}>
              <Image.PreviewGroup>
                {selectedApplicant.modelProfile.photos.map((photo, index) => (
                  <Image
                    key={index}
                    src={`${PHOTO_URL}${photo}`}
                    alt={`照片 ${index + 1}`}
                    width={100}
                    height={100}
                    style={{
                      objectFit: 'cover',
                      marginRight: 8
                    }}
                  />
                ))}
              </Image.PreviewGroup>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </Container>
  )
}

export default Applicants
